import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { inr, fdt } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { StatCard } from "@/components/pawkt/StatCard";
import { SearchInput } from "@/components/pawkt/SearchInput";
import { CreditCard, Wallet, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ApprovalRequestButton } from "@/components/pawkt/ApprovalRequestButton";
import { useRangeParams, apiQueryString, DrillDownBanner } from "@/lib/drilldown";
import { PendingRefunds } from "@/components/pawkt/PendingRefunds";

export default function Payments() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canRefund = (user?.effective_permissions || []).includes("*") || (user?.effective_permissions || []).includes("payments.refund");
  const range = useRangeParams();
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get("search") || "");

  const { data, isLoading } = useQuery({
    queryKey: ["payments", search, range.period, range.start, range.end],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      if (range.period) {
        params.period = range.period;
        if (range.period === "custom") { params.start = range.start; params.end = range.end; }
      }
      return (await api.get(`/payments${apiQueryString(params)}`)).data;
    },
  });
  const refund = useMutation({
    mutationFn: (id) => api.post(`/payments/${id}/refund`, { reason: "manual" }, { headers: { "Idempotency-Key": `${id}:${Date.now()}` } }),
    onSuccess: ({ data: r }) => {
      ["payments", "dashboard", "refunds-pending"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      if (r.refund_status === "pending") toast.warning(r.message || "Refund pending gateway confirmation");
      else if (r.refund_status === "failed" || r.ok === false) toast.error("Refund failed — nothing was refunded");
      else toast.success("Refund completed");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const confirmRefund = (p) => { if (window.confirm(`Refund ${inr(p.amount - (p.refunded_amount || 0))} on ${p.invoice_number}? The original payment stays on record.`)) refund.mutate(p.id); };

  const items = data?.items || [];
  // money received (incl. later-refunded payments); refunds shown separately (finance_metrics definitions)
  const collected = items.filter((p) => ["paid", "partially_refunded", "refunded"].includes(p.status)).reduce((s, p) => s + p.amount, 0);
  const refunded = items.reduce((s, p) => s + (p.refunded_amount || 0), 0);

  return (
    <div>
      <PageHeader title="Payments" subtitle="All transactions across methods & gateways" />
      <DrillDownBanner metric="collections" count={data?.total} />
      <div className="mb-4 max-w-md">
        <SearchInput value={search} onChange={setSearch} testid="payments-search"
          placeholder="Search invoice no., reference, customer or method…" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <StatCard testid="pay-collected" label="Total Received" value={inr(collected)} icon={Wallet} accent="emerald" />
        <StatCard testid="pay-count" label="Transactions" value={items.length} icon={CreditCard} accent="indigo" />
        <StatCard testid="pay-refunded" label="Refunded (separate)" value={inr(refunded)} icon={RotateCcw} accent="rose" />
      </div>
      {canRefund && <PendingRefunds />}

      {isLoading ? <Loading /> : (items.length ? (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Method</th><th className="px-4 py-3 hidden sm:table-cell">When</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`payment-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{p.invoice_number}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{p.customer_name}</td>
                  <td className="px-4 py-3 uppercase text-slate-600 text-xs">{p.method} · {p.provider}</td>
                  <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{fdt(p.created_at)}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular">{inr(p.amount)}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {canRefund && ["paid", "partially_refunded"].includes(p.status) && <button onClick={() => confirmRefund(p)} disabled={refund.isPending} data-testid={`refund-${p.id}`} className="text-xs text-rose-600 hover:underline">Refund</button>}
                    {p.status === "paid" && <ApprovalRequestButton action="payments.refund" targetId={p.id} targetLabel={p.invoice_number || p.id} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <Empty icon={CreditCard} title={search ? "No matching payments found" : "No payments yet"} />)}
    </div>
  );
}
